import type { Trade, TradesEventData } from "@ekidenfi/ts-sdk";

import { BN } from "@utils/BN";
import { makeAutoObservable } from "mobx";

import type { RootStore } from "./RootStore";

const MAX_TRADES = 100;

export class TradesStore {
  trades: Trade[] = [];

  // Persistent Set for deduplication - avoids recreation on every message
  private tradeIds = new Set<string>();

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  private getTradeId = (trade: Trade) => `${trade.T}_${trade.p}_${trade.v}`;

  clearTrades = () => {
    this.trades = [];
    this.tradeIds.clear();
  };

  processMessage = (event: TradesEventData) => {
    const { marketStore } = this.rootStore;
    const eventTrades = Array.isArray(event) ? event : [];

    if (eventTrades.length === 0) return;

    // Filter new trades using persistent Set
    const newTrades: Trade[] = [];
    for (const trade of eventTrades) {
      const tradeId = this.getTradeId(trade);
      if (!this.tradeIds.has(tradeId)) {
        newTrades.push(trade);
        this.tradeIds.add(tradeId);
      }
    }

    if (newTrades.length === 0) return;

    // Prepend new trades (already sorted by time from server)
    // Only sort if newTrades has multiple items
    if (newTrades.length > 1) {
      newTrades.sort((a, b) => b.T - a.T);
    }

    // Merge and trim
    const merged = [...newTrades, ...this.trades];
    if (merged.length > MAX_TRADES) {
      // Remove excess from tradeIds Set
      for (let i = MAX_TRADES; i < merged.length; i++) {
        this.tradeIds.delete(this.getTradeId(merged[i]));
      }
      this.trades = merged.slice(0, MAX_TRADES);
    } else {
      this.trades = merged;
    }

    const latestFillPrice = this.trades[0]?.p ?? "0";
    marketStore.setLatestFillPrice(new BN(latestFillPrice));
  };
}
